import { Box, Divider, MenuItem } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  selectIsAdmin,
  selectOrganizationName,
  selectOrganizations,
  setActiveOrganizationId,
  selectActiveOrganizationId,
  selectIsDisabled,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { SearchInput, UserManagementIcon } from '@/shared/ui';
import { BottomItem, BottomMenuItem } from '../BottomItem';
import { ORG_COLORS, OrgAvatar } from './OrgAvatar';
import { OrgIcon } from './OrgIcon';

export function OrgBottomItem() {
  const dispatch = useAppDispatch();
  const isUserDisabled = useAppSelector(selectIsDisabled);
  const organizationName = useAppSelector(selectOrganizationName);
  const organizations = useAppSelector(selectOrganizations);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const isAdmin = useAppSelector(selectIsAdmin);

  const sortedOrganizations = useMemo(() => {
    return organizations?.toSorted((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );
  }, [organizations]);

  const [orgSearch, setOrgSearch] = useState('');

  const handleChangeOrganization = (organizationId: string) => () => {
    dispatch(setActiveOrganizationId(organizationId));
  };

  const filteredOrganizations = useMemo(() => {
    if (!orgSearch) return sortedOrganizations;
    const lower = orgSearch.toLowerCase();
    return sortedOrganizations.filter((o) =>
      o.name.toLowerCase().includes(lower),
    );
  }, [sortedOrganizations, orgSearch]);

  const orgLabel = organizationName ?? '';
  const searchShown = sortedOrganizations.length > 5;

  const menuItems = [
    ...(sortedOrganizations.length > 1
      ? [
          <Box key="organizations-list" sx={{ mx: -1, mt: -1, p: 1 }}>
            {searchShown && (
              <SearchInput
                placeholder="Search"
                sx={{ mb: 1 }}
                query={orgSearch}
                onChange={setOrgSearch}
              />
            )}
            <Box
              sx={{
                height: searchShown ? 180 : undefined,
                maxHeight: 180,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {filteredOrganizations.length === 0 && (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: 14,
                  }}
                >
                  No organizations found
                </Box>
              )}
              {filteredOrganizations.map((organization, organizationIndex) => (
                <MenuItem
                  key={organization.id}
                  selected={activeOrganizationId === organization.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderRadius: 2,
                    p: 1,
                  }}
                  onClick={handleChangeOrganization(organization.id)}
                >
                  <OrgAvatar
                    name={organization.name}
                    color={ORG_COLORS[organizationIndex % ORG_COLORS.length]}
                  />
                  {organization.name}
                </MenuItem>
              ))}
            </Box>
          </Box>,
          <Divider key="organizations-divider" sx={{ mx: -1, mb: 1 }} />,
        ]
      : []),
    ...(isUserDisabled
      ? []
      : [
          <BottomMenuItem
            key="organization-info"
            icon={<OrgIcon />}
            label="Organization Info"
            component={Link}
            to={ROUTES.organizationInfo}
          />,
        ]),
    ...(isAdmin
      ? [
          <BottomMenuItem
            key="user-management"
            icon={<UserManagementIcon />}
            label="User Management"
            component={Link}
            to={ROUTES.userManagement}
          />,
        ]
      : []),
  ];

  return (
    <BottomItem
      icon={<OrgAvatar name={orgLabel} />}
      label={orgLabel}
      menuContent={menuItems}
      onMenuClose={() => setOrgSearch('')}
    />
  );
}
